import { useEffect, useMemo, useState } from 'react';
import { SQUAD_PAGE_HEADER, SQUAD_BODY_LAYOUT, splitSquadTitle } from './pageHeaderTokens';
import { SQUAD_FONT_FILES, SQUAD_FONT_STACK } from './templateConstants';

/** A4 width (pt) — mirrors PdfPreviewDialog/styles.ts. */
const PDF_A4_WIDTH_PT = 595.28;

/**
 * Width of the title column in the PDF header (pt):
 * A4 minus page padding, minus the logo column (sidebar width) and the column gap.
 */
export const SQUAD_PDF_TITLE_WIDTH_PT =
  PDF_A4_WIDTH_PT -
  SQUAD_PAGE_HEADER.pdfPagePaddingHorizontal * 2 -
  SQUAD_BODY_LAYOUT.sidebarMaxWidth -
  SQUAD_BODY_LAYOUT.columnGap;

/**
 * Width of the title column in the web preview (px).
 * Page is 794 px wide with 22 px padding (PdfPreview/styles.ts), minus the logo
 * column and the column gap — same layout as the PDF header.
 */
export const SQUAD_PREVIEW_TITLE_WIDTH_PX =
  794 - 22 * 2 - SQUAD_BODY_LAYOUT.sidebarMaxWidth - SQUAD_BODY_LAYOUT.columnGap;

/** Floor — below this the title is not shrunk further and may wrap instead. */
export const SQUAD_TITLE_MIN_FONT_SIZE = 16;

/** Reference size for width measurement; text width scales linearly with font size. */
const MEASURE_SIZE = 100;

/**
 * Dedicated family used only for measuring. The browser does not necessarily know
 * "SegoeUI" from CSS — canvas then measures with a fallback font that runs much
 * narrower than the TTF react-pdf embeds, and the title wraps despite a "fitting"
 * calculation. So load the same files Font.register uses.
 */
const MEASURE_FAMILY = 'SquadTitleMeasure';

let measureFontPromise: Promise<boolean> | null = null;
let measureFontReady = false;

/**
 * Load the Segoe UI files used for width measurement. Idempotent; the result tells
 * whether measuring with the real font is possible.
 */
export function ensureSquadTitleMeasureFont(): Promise<boolean> {
  if (measureFontPromise) return measureFontPromise;
  measureFontPromise = (async () => {
    if (typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) {
      return false;
    }
    try {
      const faces = [
        new FontFace(MEASURE_FAMILY, `url(${SQUAD_FONT_FILES.regular})`, { weight: '400' }),
        new FontFace(MEASURE_FAMILY, `url(${SQUAD_FONT_FILES.semibold})`, { weight: '600' }),
        new FontFace(MEASURE_FAMILY, `url(${SQUAD_FONT_FILES.bold})`, { weight: '700' }),
      ];
      await Promise.all(
        faces.map(async (face) => {
          await face.load();
          document.fonts.add(face);
        }),
      );
      measureFontReady = true;
      return true;
    } catch {
      return false;
    }
  })();
  return measureFontPromise;
}

/** Fallback character width (share of font size) when no canvas is available. */
const APPROX_CHAR_WIDTH = 0.54;

let cachedCtx: CanvasRenderingContext2D | null | undefined;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (cachedCtx !== undefined) return cachedCtx;
  try {
    cachedCtx = document.createElement('canvas').getContext('2d');
  } catch {
    cachedCtx = null;
  }
  return cachedCtx;
}

/** Weight the title is rendered in — measurement has to match it. */
const TITLE_WEIGHT = 600;

/** Width of `text` at MEASURE_SIZE px. Both title parts render in one weight. */
function measureAtReferenceSize(text: string): number {
  if (!text) return 0;
  const ctx = getMeasureContext();
  if (!ctx) {
    return text.length * APPROX_CHAR_WIDTH * MEASURE_SIZE;
  }
  const family = measureFontReady ? `${MEASURE_FAMILY}, ${SQUAD_FONT_STACK}` : SQUAD_FONT_STACK;
  ctx.font = `${TITLE_WEIGHT} ${MEASURE_SIZE}px ${family}`;
  const width = ctx.measureText(text).width;
  // No measurable width (font not loaded yet) → approximate instead of returning 0
  return width > 0 ? width : text.length * APPROX_CHAR_WIDTH * MEASURE_SIZE;
}

export interface SquadTitleFitOptions {
  /** Desired size; defaults to the original factsheet title size. */
  maxSize?: number;
  /** Floor; below this the title may wrap. */
  minSize?: number;
}

/**
 * Shrink the page title until it fits on a single line — the same idea as the
 * PowerPoint autofit SQUAD produces the original factsheet with (a longer fund name
 * is single-line there too).
 *
 * Without this a three-line title is covered by the first section bar: the header
 * reserves a fixed height that assumes a single title line.
 *
 * @param title    Full page title, e.g. "SQUAD Aguja Bond Opportunities - Seed"
 * @param maxWidth Available width of the title column (pt in the PDF, px in the preview)
 */
export function fitSquadTitleFontSize(
  title: string,
  maxWidth: number = SQUAD_PDF_TITLE_WIDTH_PT,
  options: SquadTitleFitOptions = {},
): number {
  const { maxSize = SQUAD_PAGE_HEADER.titleFontSize, minSize = SQUAD_TITLE_MIN_FONT_SIZE } =
    options;

  const full = String(title || '');
  if (!full.trim()) return maxSize;

  const { squad, rest } = splitSquadTitle(full);
  // Rendering: "SQUAD" in gold, the rest in the primary colour — same weight (SquadPdfPageHeader)
  const head = squad || '';
  const tail = squad ? (rest ? ` ${rest}` : '') : full;

  const widthAtReference =
    measureAtReferenceSize(head) + measureAtReferenceSize(tail);
  if (!widthAtReference) return maxSize;

  // letterSpacing is absolute (pt in the PDF, px in the preview) and does not scale
  // with the font size: width(size) = size * widthPerSize + spacing * chars
  const chars = head.length + tail.length;
  const spacing = SQUAD_PAGE_HEADER.titleLetterSpacing * chars;
  const widthPerSize = widthAtReference / MEASURE_SIZE;
  const raw = (maxWidth - spacing) / widthPerSize;

  if (!Number.isFinite(raw) || raw <= 0) return minSize;

  // Canvas and react-pdf round line breaking slightly differently — keep 1.5 % headroom
  // so a borderline title does not slip into a second line after all.
  const fitted = raw * 0.985;
  return Math.round(Math.min(maxSize, Math.max(minSize, fitted)) * 100) / 100;
}

/**
 * Title size for React components. Loads the measurement font and recomputes once it
 * is there — otherwise the first pass measures with a fallback font and the title
 * comes out too large.
 */
export function useSquadTitleFontSize(
  title: string,
  maxWidth: number = SQUAD_PDF_TITLE_WIDTH_PT,
  options: SquadTitleFitOptions = {},
): number {
  const { maxSize, minSize } = options;
  const [fontReady, setFontReady] = useState(measureFontReady);

  useEffect(() => {
    if (fontReady) return undefined;
    let cancelled = false;
    ensureSquadTitleMeasureFont().then((ok) => {
      if (!cancelled && ok) setFontReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fontReady]);

  return useMemo(
    () => fitSquadTitleFontSize(title, maxWidth, { maxSize, minSize }),
    // fontReady re-triggers the calculation once the real font can be measured
    [title, maxWidth, maxSize, minSize, fontReady],
  );
}
